/**
 * Single source of truth for per-page SEO metadata.
 *
 * CommonJS on purpose: `scripts/generate-seo-pages.js` requires this at build time to bake tags into
 * static HTML, and the React app imports it for client-side updates during SPA navigation. If the
 * two ever disagreed, Google would index one thing and users would see another.
 *
 * Two rules shaped the copy below:
 *
 *   Search the way players do. Nobody searches "Old School RuneScape currency" — they search "OSRS
 *   gold". Titles therefore lead with the real in-game currency name where one exists, which is
 *   what GAME_CURRENCY_TERMS is for.
 *
 *   Keep the keyword ahead of the brand. Google truncates titles around 60 characters, so the brand
 *   suffix goes last where it can be cut without losing the term being ranked for.
 */

const { gameNameMap } = require('./gameNames');

const SITE_NAME = 'XMARKET.GG';
const SITE_ORIGIN = process.env.SITEMAP_ORIGIN || 'https://www.xmarket.gg';
const DEFAULT_OG_IMAGE = '/logo512.png';

/** Short forms that outrank the full game name in search volume. */
const TITLE_LABEL_OVERRIDES = {
  // Kept distinct so the two Siege editions do not compete with identical titles.
  'rainbow-six-siege': 'R6 Siege',
  'rainbow-six-siege-x': 'R6 Siege X',
  'old-school-runescape': 'OSRS',
  'counter-strike-2': 'CS2',
  cs2: 'CS2',
  'world-of-warcraft': 'WoW',
  warcraft: 'WoW',
  'wow-classic': 'WoW Classic',
  'league-of-legends': 'LoL',
  'cod-call-of-duty': 'CoD',
  'call-of-duty': 'CoD',
  'grand-theft-auto-v': 'GTA 5',
  'final-fantasy-xiv': 'FFXIV',
  'elder-scrolls-online': 'ESO',
  'path-of-exile': 'PoE',
  'path-of-exile-2': 'PoE 2',
  'mobile-legends': 'Mobile Legends',
  'escape-from-tarkov': 'Tarkov',
  'dead-by-daylight': 'DBD',
  'genshin-impact': 'Genshin Impact',
  'black-desert-online': 'BDO',
  'ea-sports-fc': 'EA FC',
  runescape3: 'RS3',
  'wow-mists-of-pandaria': 'WoW MoP',
  'league-of-legends-wild-rift': 'Wild Rift',
};

/**
 * The name players actually use for a game's currency, so the title reads "Buy OSRS Gold" rather
 * than "Buy Old School RuneScape Currency". `topUp` covers the premium currency sold as a top-up
 * where it differs from the tradeable one.
 */
const GAME_CURRENCY_TERMS = {
  'old-school-runescape': { currency: 'OSRS Gold' },
  runescape3: { currency: 'RS3 Gold' },
  'world-of-warcraft': { currency: 'WoW Gold' },
  warcraft: { currency: 'WoW Gold' },
  'wow-classic': { currency: 'WoW Classic Gold' },
  'wow-mists-of-pandaria': { currency: 'WoW MoP Gold' },
  fortnite: { topUp: 'V-Bucks' },
  roblox: { currency: 'Robux', topUp: 'Robux' },
  valorant: { topUp: 'Valorant Points' },
  'league-of-legends': { topUp: 'League of Legends RP' },
  'league-of-legends-wild-rift': { topUp: 'Wild Cores' },
  'genshin-impact': { topUp: 'Genesis Crystals' },
  'honkai-star-rail': { topUp: 'Oneiric Shards' },
  'mobile-legends': { topUp: 'Diamonds' },
  'free-fire': { topUp: 'Diamonds' },
  pubg: { topUp: 'UC' },
  'pubg-mobile': { topUp: 'UC' },
  'clash-of-clans': { topUp: 'Gems' },
  'clash-royale': { topUp: 'Gems' },
  'brawl-stars': { topUp: 'Gems' },
  'grand-theft-auto-v': { currency: 'GTA 5 Money' },
  'escape-from-tarkov': { currency: 'Roubles' },
  'path-of-exile': { currency: 'PoE Currency' },
  'path-of-exile-2': { currency: 'PoE 2 Currency' },
  'diablo-4': { currency: 'Diablo 4 Gold' },
  'lost-ark': { currency: 'Lost Ark Gold' },
  'final-fantasy-xiv': { currency: 'FFXIV Gil' },
  'elder-scrolls-online': { currency: 'ESO Gold' },
  'albion-online': { currency: 'Albion Online Silver' },
  'new-world': { currency: 'New World Coins' },
  'black-desert-online': { currency: 'BDO Silver' },
  'rocket-league': { currency: 'Rocket League Credits' },
  'ea-sports-fc': { currency: 'EA FC Coins' },
  'grow-a-garden': { currency: 'Sheckles' },
  donutsmp: { currency: 'DonutSMP Money' },
  hypixel: { currency: 'Hypixel Coins' },
  'throne-and-liberty': { currency: 'Lucent' },
  'anime-vanguards': { currency: 'Gems' },
};

const titleCaseSlug = (slug) =>
  String(slug || '')
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/** Full display name, e.g. "Old School RuneScape". */
function getGameName(slug) {
  if (!slug) return '';
  return gameNameMap[slug] || titleCaseSlug(slug);
}

/** Short label preferred in titles, e.g. "OSRS". */
function getGameLabel(slug) {
  if (!slug) return '';
  return TITLE_LABEL_OVERRIDES[slug] || gameNameMap[slug] || titleCaseSlug(slug);
}

/**
 * Ensures a currency term names its game, so titles read "Tarkov Roubles" and "Mobile Legends
 * Diamonds" rather than the ambiguous "Roubles" or "Diamonds" nobody searches for on its own.
 * Terms that already carry the game (e.g. "OSRS Gold") are left alone to avoid stutter.
 */
function qualifyTerm(term, slug) {
  if (!term) return '';
  const haystack = term.toLowerCase();
  const label = getGameLabel(slug);

  const alreadyNamed =
    haystack.includes(label.toLowerCase()) ||
    haystack.includes(getGameName(slug).toLowerCase()) ||
    // Catches cases where the display name is broader than the term, e.g. slug "hypixel" displaying
    // as "Minecraft Hypixel" while the currency is simply "Hypixel Coins".
    String(slug || '')
      .split('-')
      .filter((token) => token.length > 2)
      .some((token) => haystack.includes(token));

  return alreadyNamed ? term : `${label} ${term}`;
}

/** The tradeable currency term, falling back to "<Game> Currency". */
function getCurrencyTerm(slug) {
  const terms = GAME_CURRENCY_TERMS[slug];
  if (terms?.currency) return qualifyTerm(terms.currency, slug);
  return `${getGameLabel(slug)} Currency`;
}

/**
 * The top-up term, falling back to the currency term and then to the bare game label.
 * The label alone is the fallback because callers append "Top Up" themselves.
 */
function getTopUpTerm(slug) {
  const terms = GAME_CURRENCY_TERMS[slug];
  if (terms?.topUp) return qualifyTerm(terms.topUp, slug);
  if (terms?.currency) return qualifyTerm(terms.currency, slug);
  return getGameLabel(slug);
}

/**
 * Google truncates titles around 70 characters and descriptions around 160. Game names here range
 * from "Rust" to "JoJo's Bizarre Adventure: Ora Ora Overdrive", so a single fixed template would
 * either waste space on short names or get the keyword cut off on long ones. The two composers below
 * fit the copy to the budget instead: the part that identifies the page is always kept, and the
 * persuasive extras are added only while they fit.
 */
const TITLE_BUDGET = 70;
const DESCRIPTION_BUDGET = 160;

/** Keeps `base`, adds the modifier and the brand only while the whole title still fits. */
function composeTitle(base, modifier) {
  const brand = ` | ${SITE_NAME}`;
  const withModifier = modifier ? `${base} ${modifier}` : base;

  if ((withModifier + brand).length <= TITLE_BUDGET) return withModifier + brand;
  if (withModifier.length <= TITLE_BUDGET) return withModifier;
  if ((base + brand).length <= TITLE_BUDGET) return base + brand;
  return base;
}

/** Keeps `primary`, then appends each extra clause while the description still fits. */
function composeDescription(primary, extras = []) {
  return extras.reduce((text, extra) => {
    const next = `${text} ${extra}`;
    return next.length <= DESCRIPTION_BUDGET ? next : text;
  }, primary);
}

const PAGE_TYPES = ['home', 'game', 'currency', 'accounts', 'items', 'top-ups', 'boosting'];

/**
 * Slugs that serve the same game under a second URL, mapped to the slug we want to rank.
 *
 * The duplicates stay reachable because navigation still links to them; instead of redirecting, they
 * carry a canonical tag pointing here and are kept out of the sitemap, which consolidates the
 * ranking signals onto one URL. Resolution happens in scripts/lib/seoRoutes.js, which can also check
 * that the preferred URL exists.
 */
const ALIAS_SLUGS = {
  cs2: 'counter-strike-2',
  warcraft: 'world-of-warcraft',
  'call-of-duty': 'cod-call-of-duty',
};

/**
 * Builds title and description for a page.
 *
 * Descriptions sit in the 140–160 character band so Google shows them whole, and each one carries a
 * price, safety and delivery signal because those are the three things a buyer scans for.
 *
 * @param {string} pageType One of PAGE_TYPES
 * @param {string} [slug] Game slug, required for everything except 'home'
 * @returns {{ title: string, description: string }}
 */
function buildMeta(pageType, slug) {
  const label = getGameLabel(slug);
  const name = getGameName(slug);

  switch (pageType) {
    case 'home':
      return {
        title: `Buy & Sell Game Accounts, Currency, Items & Boosting | ${SITE_NAME}`,
        description:
          'Trade game accounts, currency, items, top-ups and boosting with verified sellers. Escrow-protected payments, instant delivery and 24/7 support on XMARKET.GG.',
      };

    case 'game':
      return {
        title: composeTitle(`${label} Accounts, Items & Currency`, 'for Sale'),
        description: composeDescription(
          `Buy and sell ${name} accounts, items, currency and boosting from verified sellers.`,
          ['Escrow-protected with instant delivery.', `Low fees and 24/7 support on ${SITE_NAME}.`]
        ),
      };

    case 'currency': {
      // The term already names the game, so the copy must not repeat it.
      const term = getCurrencyTerm(slug);
      return {
        title: composeTitle(`Buy ${term}`, '— Cheap, Safe & Instant'),
        description: composeDescription(
          `Buy cheap ${term} from verified sellers with instant delivery.`,
          [
            'Compare live prices and pay securely with escrow protection.',
            `Low fees on ${SITE_NAME}.`,
          ]
        ),
      };
    }

    case 'accounts':
      return {
        title: composeTitle(`Buy ${label} Accounts`, '— Cheap, Safe & Instant'),
        description: composeDescription(
          `Buy ${name} accounts from verified sellers at the best prices.`,
          [
            'Every purchase is escrow-protected with instant delivery and a warranty.',
            `Trade safely on ${SITE_NAME}.`,
          ]
        ),
      };

    case 'items':
      return {
        title: composeTitle(`Buy ${label} Items & Skins`, '— Cheap & Safe'),
        description: composeDescription(
          `Buy ${name} items, skins and gear from verified sellers.`,
          [
            'Compare live prices and trade safely with escrow protection.',
            `Fast delivery on ${SITE_NAME}.`,
          ]
        ),
      };

    case 'top-ups': {
      const term = getTopUpTerm(slug);
      return {
        title: composeTitle(`${term} Top Up`, '— Cheap & Instant Recharge'),
        description: composeDescription(
          `Top up ${term} at cheap prices with instant delivery.`,
          [
            'Pay by card, PayPal or crypto and get recharged in minutes.',
            `24/7 support on ${SITE_NAME}.`,
          ]
        ),
      };
    }

    case 'boosting':
      return {
        title: composeTitle(`${label} Boosting`, '— Rank Boost & Coaching'),
        description: composeDescription(
          `Hire verified ${name} boosters for rank boosting, coaching and carries.`,
          [
            'Compare offers and chat before you pay.',
            `Escrow-protected on ${SITE_NAME}.`,
          ]
        ),
      };

    default:
      return {
        title: `Buy & Sell Game Accounts, Currency, Items & Boosting | ${SITE_NAME}`,
        description:
          'Trade game accounts, currency, items, top-ups and boosting with verified sellers. Escrow-protected payments, instant delivery and 24/7 support on XMARKET.GG.',
      };
  }
}

/** Human-readable label for the service segment, used in breadcrumbs. */
const SERVICE_LABELS = {
  currency: 'Currency',
  accounts: 'Accounts',
  items: 'Items',
  'top-ups': 'Top Ups',
  boosting: 'Boosting',
};

/**
 * Maps a URL path to its page type and game slug.
 *
 * @param {string} pathname
 * @returns {{ pageType: string, slug: string|null }|null} null when the route is not a public
 *   catalog page (profile, checkout, and friends), which must not receive catalog metadata.
 */
const NON_GAME_SEGMENTS = new Set([
  'profile',
  'auth',
  'admin',
  'mod',
  'messages',
  'cart',
  'checkout',
  'orders',
  'my-requests',
  'boosting',
  'ad',
  'users',
]);

/**
 * Signed-in and transactional areas. Kept in step with public/robots.txt: robots.txt stops the
 * crawl, this list adds a noindex meta tag for anything that gets discovered through a link anyway,
 * since a disallowed URL can still be indexed on the strength of inbound links alone.
 */
const PRIVATE_PREFIXES = [
  '/profile',
  '/auth',
  '/admin',
  '/mod',
  '/messages',
  '/my-requests',
  '/cart',
  '/checkout',
  '/orders',
  '/boosting/requests',
  '/boosting/checkout',
  '/boosting/offers',
];

function isPrivatePath(pathname) {
  const clean = String(pathname || '/').split('?')[0];
  return PRIVATE_PREFIXES.some((p) => clean === p || clean.startsWith(`${p}/`));
}

function resolvePage(pathname) {
  const clean = String(pathname || '/').split('?')[0].split('#')[0];
  const segments = clean.split('/').filter(Boolean);

  if (segments.length === 0) return { pageType: 'home', slug: null };
  if (NON_GAME_SEGMENTS.has(segments[0])) return null;

  const slug = segments[0];
  if (segments.length === 1) return { pageType: 'game', slug };

  const service = segments[1];
  if (segments.length === 2 && SERVICE_LABELS[service]) {
    return { pageType: service, slug };
  }

  return null;
}

const absoluteUrl = (pathname) => {
  const base = SITE_ORIGIN.replace(/\/$/, '');
  if (!pathname || pathname === '/') return `${base}/`;
  return `${base}/${String(pathname).replace(/^\/+/, '')}`;
};

module.exports = {
  SITE_NAME,
  SITE_ORIGIN,
  DEFAULT_OG_IMAGE,
  PAGE_TYPES,
  SERVICE_LABELS,
  ALIAS_SLUGS,
  PRIVATE_PREFIXES,
  isPrivatePath,
  GAME_CURRENCY_TERMS,
  TITLE_LABEL_OVERRIDES,
  getGameName,
  getGameLabel,
  qualifyTerm,
  getCurrencyTerm,
  getTopUpTerm,
  buildMeta,
  resolvePage,
  absoluteUrl,
};
